import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';

const AGENT_AWARE_CLASSNAMES = {
    AGENT_AWARE_HIDE: 'agent-aware-hide agent-aware-hide-fallback',
    AGENT_AWARE_SHOW: 'agent-aware-show agent-aware-show-fallback'
};

// Cache for decoded JWT to avoid repeated decoding
let cachedToken = null;
let cachedDecodedPayload = null;

/**
 * Decodes a JWT token and caches the result
 * @param {string} token - JWT token to decode
 * @returns {Object|null} Decoded payload or null if invalid
 */
function decodeJWT(token) {
    if (!token) {
        return null;
    }

    // Return cached result if token hasn't changed
    if (token === cachedToken && cachedDecodedPayload) {
        return cachedDecodedPayload;
    }

    try {
        const parts = token.split('.');

        if (parts.length !== 3) {
            throw new Error('Invalid JWT token');
        }

        // Base64URL decode the payload
        const base64Url = parts[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(atob(base64));

        // Cache the result
        cachedToken = token;
        cachedDecodedPayload = payload;

        return payload;
    } catch (error) {
        // Clear cache on error
        cachedToken = null;
        cachedDecodedPayload = null;

        return null;
    }
}

/**
 * Gets the decoded JWT from local storage
 * @returns {Object|null} Decoded JWT payload or null
 */
function getDecodedJWT() {
    if (typeof window === 'undefined') {
        return null;
    }

    const jwt = Storage.local.getItem(LOCAL_STORAGE.AUTH_ACCESS_TOKEN);

    return decodeJWT(jwt);
}

/**
 * Checks if the current agent is authorized for the specified tiers
 * @param {string[]} agentTierArray - Array of allowed agent tiers
 * @returns {boolean} True if authorized
 */
export const isAgentAuthorized = agentTierArray => {

    // Use extension method if available
    if (typeof Sephora !== 'undefined' && Sephora.isAgentAuthorizedRole) {
        return Sephora.isAgentAuthorizedRole(agentTierArray);
    }

    const decoded = getDecodedJWT();

    if (!decoded?.AuthData?.role) {
        return false;
    }

    const userRoles = decoded.AuthData.role;

    return agentTierArray?.includes(userRoles);
};

/**
 * Checks if the current session is an agent aware session
 * @returns {boolean} True if agent aware session
 */
export const isAgentAwareSession = () => {

    // Use extension method if available
    if (typeof Sephora !== 'undefined' && Sephora.isAgentAuthorizedRole) {
        return true;
    }

    const decoded = getDecodedJWT();

    if (!decoded?.AuthData?.type) {
        return false;
    }

    return decoded.AuthData.type === 'agent';
};

/**
 * Applies a classname if the session is agent aware
 * @param {string} className - Classname to apply
 * @returns {string} The classname or empty string
 */
export const applyAgentAwareClassname = (className = '') => {
    return isAgentAwareSession() ? className : '';
};

/**
 * Applies a classname if the agent is authorized for the specified tiers
 * @param {string} className - Classname to apply
 * @param {string[]} tiersAllowed - Array of allowed tiers
 * @returns {string} The classname or empty string
 */
export const applyAgentAwareClassnameToTiers = (className = '', tiersAllowed = []) => {
    return isAgentAuthorized(tiersAllowed) ? className : '';
};

/**
 * Applies the hide classname for agent aware sessions
 * @returns {string} Hide classname or empty string
 */
export const applyHideAgentAwareClass = () => {
    return applyAgentAwareClassname(AGENT_AWARE_CLASSNAMES.AGENT_AWARE_HIDE);
};

/**
 * Applies the show classname for agent aware sessions
 * @returns {string} Show classname or empty string
 */
export const applyShowAgentAwareClass = () => {
    return applyAgentAwareClassname(AGENT_AWARE_CLASSNAMES.AGENT_AWARE_SHOW);
};

/**
 * Applies the hide classname for specific agent tiers
 * @param {string[]} tiersAllowed - Array of allowed tiers
 * @returns {string} Hide classname or empty string
 */
export const applyHideAgentAwareClassToTiers = (tiersAllowed = []) => {
    return applyAgentAwareClassnameToTiers(AGENT_AWARE_CLASSNAMES.AGENT_AWARE_HIDE, tiersAllowed);
};

/**
 * Adds an event listener for agent aware events (SSR-safe)
 * @param {string} eventName - Event name
 * @param {Function} callbackFn - Callback function
 */
export const addAgentAwareListener = (eventName, callbackFn) => {
    if (typeof window !== 'undefined') {
        window.addEventListener(eventName, callbackFn);
    }
};

/**
 * Removes an event listener for agent aware events (SSR-safe)
 * @param {string} eventName - Event name
 * @param {Function} eventListener - Event listener function
 */
export const removeAgentAwareListener = (eventName, eventListener) => {
    if (typeof window !== 'undefined') {
        window.removeEventListener(eventName, eventListener, false);
    }
};

// Default export for backward compatibility
export default {
    applyShowAgentAwareClass,
    applyAgentAwareClassnameToTiers,
    applyHideAgentAwareClass,
    applyHideAgentAwareClassToTiers,
    addAgentAwareListener,
    removeAgentAwareListener,
    isAgentAuthorized,
    isAgentAwareSession
};
